import { Button } from 'rsuite'
import useTranslation from '@/hooks/useTranslation'
import styles from './skipToContent.module.scss'

/**
 * SkipToContent - Accessibility component that allows keyboard users
 * to skip navigation and go directly to main content.
 *
 * Best practices:
 * - Should be one of the first focusable elements on the page
 * - Hidden until focused via keyboard navigation
 * - Target element should have tabIndex={-1} for proper focus handling
 *
 * Priority-based targeting:
 * - First checks for `primaryTargetId` element (e.g., page-specific content like TOC)
 * - Falls back to `fallbackTargetId` if primary doesn't exist (default: 'main-content')
 * - This allows pages to override the skip target by adding id="primary-content" to their preferred element
 *
 * @param {string} buttonLabelKey - Translation key for button text (default: 'header_skip_to_content')
 * @param {string} buttonLabelDefault - Fallback text if translation not found (default: 'Skip to main content')
 * @param {string} primaryTargetId - Priority target ID, used if element exists (default: 'primary-content')
 * @param {string} fallbackTargetId - Fallback target ID if primary doesn't exist (default: 'main-content')
 * @param {string} className - Additional CSS class names
 */
function SkipToContent({
  buttonLabelKey = 'header_skip_to_content',
  buttonLabelDefault = 'Skip to content',
  primaryTargetId = 'primary-content',
  fallbackTargetId = 'main-content',
  className = ''
}) {
  const t = useTranslation()

  const getTargetElement = () => {
    const primaryTarget = document.getElementById(primaryTargetId)
    if (primaryTarget) return primaryTarget

    return document.getElementById(fallbackTargetId)
  }

  const handleClick = e => {
    e.preventDefault()
    const target = getTargetElement()
    if (target) {
      target.focus()
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <Button
      as="a"
      href={`#${fallbackTargetId}`}
      className={`${styles.skipToContent} ${className}`.trim()}
      appearance="link"
      size="sm"
      onClick={handleClick}
    >
      {t(buttonLabelKey, buttonLabelDefault)}
    </Button>
  )
}

export default SkipToContent
